<script setup lang="ts">
import { usePejer } from '@/Pejer'
import { MissingDirectRemittanceInfo } from '@/Pejer/PejerEmbeddables'
import { ConnectedAccountsEmbed } from '@/Pejer/tables/ConnectedAccounts'
import { toTypedSchema } from '@vee-validate/zod'
import { Check, Download, FileSearch, Loader2, Send } from 'lucide-vue-next'
import HorizontalLine from 'pejer/src/components/HorizontalLine.vue'
import { AutoForm } from 'pejer/src/components/ui/auto-form'
import { Button } from 'pejer/src/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
} from 'pejer/src/components/ui/dialog'
import { toast } from 'pejer/src/components/ui/toast'
import { useForm } from 'vee-validate'
import { ref, watch } from 'vue'
import { z } from 'zod'

const props = defineProps<{
  connectedAccount: ConnectedAccountsEmbed.ListItem
}>()
const emit = defineEmits(['update:close'])

const pejer = usePejer()

const formSchema = z.object({
  bankEmail: z.string().email(),
  bankContactPerson: z.string().min(1),
  textOnAccountStatement: z.string().min(1).max(30),
})

const form = useForm<MissingDirectRemittanceInfo>({
  validationSchema: toTypedSchema(formSchema),
  validateOnMount: true,
})

watch(form.values, () => {
  clickedDownload.value = false
})

const pdfSource = ref<string | null>(null)

const getPreviewPDF = async () => {
  const download = await pejer.getDirectRemittancePDF(
    props.connectedAccount.connectedAccountID,
    form.values
  )

  if ('error' in download) {
    toast({
      title:
        'Kunne ikke forhåndsvise avtalen, vennligst prøv igjen senere eller trykk på "Last ned avtale"',
      variant: 'destructive',
    })
    return
  }
  pdfSource.value = download.data
}

const loadingDownload = ref(false)
const currentLink = ref<string | null>(null)

const downloadPDF = async () => {
  loadingDownload.value = true
  const download = await pejer.getDirectRemittancePDF(
    props.connectedAccount.connectedAccountID,
    form.values
  )

  if ('error' in download) {
    loadingDownload.value = false
    toast({
      title: 'Kunne ikke laste ned avtalen, vennligst prøv igjen senere',
      variant: 'destructive',
    })
    return
  }

  const link = await pejer.saveDirectRemittance(
    props.connectedAccount.connectedAccountID,
    form.values
  )

  if ('error' in link) {
    loadingDownload.value = false
    toast({
      title: 'Kunne ikke lagre avtalen, vennligst prøv igjen senere',
      variant: 'destructive',
    })
    return
  }

  loadingDownload.value = false
  clickedDownload.value = true
  currentLink.value = link.data
  const a = document.createElement('a')
  a.href = download.data
  a.download = 'Paybucky-DirektRemittance.pdf'
  a.click()
}

const submit = async () => {
  const emailBody = `Hei ${form.values.bankContactPerson},
Vi ønsker å opprette KID-avtale på en av våre kontoer.

Gjelder følgende konto: ${props.connectedAccount.bban}

Se vedlagt "Registreringsskjema for Nets-Tjenester" eller last det ned herfra: ${currentLink.value}.
`

  const subject = `${props.connectedAccount}: opprette OCR/KID avtale på ny bankkonto`
  const mailToLink = `mailto:${form.values.bankEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(emailBody)}`
  window.open(mailToLink, '_blank')
}

const sendConfirmationLoading = ref(false)

const sendConfirmation = async () => {
  sendConfirmationLoading.value = true
  const download = await pejer.confirmNetsDealSent(props.connectedAccount.connectedAccountID)

  if ('error' in download) {
    toast({
      title: 'Kunne ikke bekrefte at avtalen er sendt, vennligst prøv igjen senere',
      variant: 'destructive',
    })
    return
  }
  sendConfirmationLoading.value = false
  toast({
    title: 'Bekreftelse på at avtalen er sendt er mottatt',
  })
  emit('update:close')
}

const clickedDownload = ref(false)
</script>
<template>
  <div class="flex flex-col gap-1">
    <h3 class="font-semibold">1. Fyll ut skjema</h3>
    <HorizontalLine class="mt-1 mb-2 bg-slate-200" />
    <AutoForm
      :schema="formSchema"
      :form="form"
      class="flex flex-col gap-4 mb-2"
      :field-config="{
        bankEmail: {
          label: 'E-post til banken',
          inputProps: {
            type: 'email',
            placeholder: '****@dnb.no',
          },
        },
        bankContactPerson: {
          label: 'Kontaktperson i banken',
          inputProps: {
            placeholder: 'Ola Nordmann',
          },
        },
        textOnAccountStatement: { label: 'Tekst på kontoutskrift' },
      }"
    />
    <Dialog @update:open="getPreviewPDF">
      <DialogTrigger as-child>
        <slot>
          <Button
            @click.prevent.stop
            variant="outline"
            class="group"
            :disabled="Object.values(form.errors.value).length > 0"
          >
            <FileSearch class="group-hover:scale-110 mr-2" :size="16" />Forhåndsvis avtale
          </Button>
        </slot>
      </DialogTrigger>
      <DialogContent class="max-w-screen max-h-[calc(100vh-25px)] flex flex-col h-full">
        <DialogTitle class="flex-1"> Forhåndsvis avtale </DialogTitle>
        <object
          v-if="pdfSource"
          :data="pdfSource"
          type="application/pdf"
          width="100%"
          height="100%"
        >
          <p>Kunne ikke forhåndsvise avtalen.</p>
          <a :href="pdfSource" class="text-blue-500 italic">Last ned avtalen istedet</a>.
        </object>
        <DialogFooter class="mt-0 flex flex-wrap gap-4 flex-1">
          <div class="flex space-x-2">
            <DialogClose as-child>
              <Button variant="outline">Lukk</Button>
            </DialogClose>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <h3 class="font-semibold mt-4">2. Last ned avtalen</h3>
    <HorizontalLine class="mt-1 mb-2 bg-slate-200" />
    <Button
      @click.prevent.stop="downloadPDF"
      variant="default"
      class="group"
      :disabled="Object.values(form.errors.value).length > 0 || loadingDownload || clickedDownload"
    >
      <Loader2 v-if="loadingDownload" class="mr-2 animate-spin" :size="16" />
      <Download v-else class="group-hover:scale-110 mr-2" :size="16" />
      Last ned avtale
    </Button>

    <h3 class="font-semibold mt-4">3. Send til banken</h3>
    <HorizontalLine class="mt-1 mb-2 bg-slate-200" />
    <span class="italic text-sm mb-2 text-gray-600"> Last opp avtalen og send til banken. </span>
    <Button
      @click.prevent.stop="submit"
      variant="default"
      class="group"
      :disabled="Object.values(form.errors.value).length > 0 || !clickedDownload"
    >
      <Send class="group-hover:scale-110 mr-2" :size="16" />Send til banken
    </Button>

    <h3 class="font-semibold mt-4">4. Gi oss en bekreftelse</h3>
    <HorizontalLine class="mt-1 mb-2 bg-slate-200" />
    <span class="italic text-sm mb-2 text-gray-600">
      Etter du har sendt avtalen til banken kan du gi oss en bekreftelse ved å trykke på knappen
      under
    </span>
    <Button
      @click.prevent.stop="sendConfirmation"
      variant="default"
      class="group"
      :disabled="
        Object.values(form.errors.value).length > 0 || !clickedDownload || sendConfirmationLoading
      "
    >
      <Loader2 v-if="sendConfirmationLoading" class="mr-2 animate-spin" :size="16" />
      <Check class="group-hover:scale-110 mr-2" :size="16" /> Bekreft at avtalen er sendt
    </Button>
  </div>
</template>
